/**
 * Explicit "Save for offline" — pre-cache an adventure's detail JSON, photos
 * and GPX into a dedicated, never-trimmed Cache Storage bucket so a saved guide
 * opens with zero signal. A lightweight index of saved guides lives in
 * localStorage for the "My Offline Guides" shelf.
 *
 * Reads are served by the service worker (public/sw.js), which checks this
 * OFFLINE_CACHE first via the global caches.match() fallback.
 */
import { resolveImageUrl } from "../components/ImageUploader";

export const OFFLINE_CACHE = "rwo-offline-v1";
const INDEX_KEY = "rwo_offline_guides";
const API = process.env.REACT_APP_BACKEND_URL;

export const offlineSupported = () =>
    typeof window !== "undefined" && "caches" in window;

const variant = (url, kind) =>
    url.replace(/(\.[a-z0-9]+)(\?.*)?$/i, `-${kind}$1$2`);

function authHeaders() {
    const h = {};
    try {
        const t = localStorage.getItem("rwo_auth_token");
        const b = localStorage.getItem("rwo_beta_code");
        if (t) h["Authorization"] = `Bearer ${t}`;
        if (b) h["X-Beta-Access"] = b;
    } catch { /* ignore */ }
    return h;
}

export function listGuides() {
    try {
        return JSON.parse(localStorage.getItem(INDEX_KEY) || "[]");
    } catch {
        return [];
    }
}

export function isSaved(id) {
    return listGuides().some((g) => g.id === id);
}

async function safePut(cache, url, res) {
    if (res && (res.ok || res.type === "opaque")) {
        try { await cache.put(url, res.clone()); } catch { /* ignore */ }
    }
}

/**
 * Cache everything needed to view `adv` offline.
 * @param {object} adv  Full adventure object (from the detail endpoint).
 * @param {(done:number,total:number)=>void} [onProgress]
 */
export async function saveGuide(adv, onProgress) {
    if (!offlineSupported()) throw new Error("Offline saving isn't supported on this browser.");
    const cache = await caches.open(OFFLINE_CACHE);

    const media = new Set();
    (adv.photos || []).forEach((p) => {
        const u = resolveImageUrl(p);
        if (u) { media.add(u); media.add(variant(u, "card")); }
    });
    const cover = resolveImageUrl(adv.cover_photo || (adv.photos || [])[0]);
    if (cover) { media.add(cover); media.add(variant(cover, "card")); media.add(variant(cover, "thumb")); }
    const avatar = resolveImageUrl(adv.author?.avatar_url);
    if (avatar) media.add(avatar);

    const detailUrl = `${API}/api/adventures/${adv.id}`;
    const gpxUrl = `${API}/api/adventures/${adv.id}/gpx`;
    const mediaList = [...media].filter(Boolean);
    const total = mediaList.length + 2; // + detail json + gpx
    let done = 0;
    const tick = () => { done += 1; onProgress?.(done, total); };

    // 1) Detail JSON (auth required)
    try {
        const res = await fetch(detailUrl, { headers: authHeaders() });
        await safePut(cache, detailUrl, res);
    } catch { /* ignore */ }
    tick();

    // 2) GPX (may 404 if the route has no coordinates — that's fine)
    try {
        const res = await fetch(gpxUrl, { headers: authHeaders() });
        await safePut(cache, gpxUrl, res);
    } catch { /* ignore */ }
    tick();

    // 3) Media (photos / avatar)
    for (const u of mediaList) {
        try {
            const res = await fetch(u, { mode: "cors", credentials: "omit" });
            await safePut(cache, u, res);
        } catch { /* ignore */ }
        tick();
    }

    const meta = {
        id: adv.id,
        title: adv.title || "Untitled adventure",
        activity: adv.activity || "",
        difficulty: adv.difficulty || "",
        location: adv.location_name || adv.general_area || "",
        cover: cover || "",
        photoCount: (adv.photos || []).length,
        savedAt: new Date().toISOString(),
        urls: [detailUrl, gpxUrl, ...mediaList],
    };
    const next = [meta, ...listGuides().filter((g) => g.id !== adv.id)];
    localStorage.setItem(INDEX_KEY, JSON.stringify(next));
    return meta;
}

export async function removeGuide(id) {
    const guides = listGuides();
    const meta = guides.find((g) => g.id === id);
    if (meta && offlineSupported()) {
        try {
            const cache = await caches.open(OFFLINE_CACHE);
            await Promise.all((meta.urls || []).map((u) => cache.delete(u).catch(() => {})));
        } catch { /* ignore */ }
    }
    localStorage.setItem(INDEX_KEY, JSON.stringify(guides.filter((g) => g.id !== id)));
}

export async function storageEstimate() {
    try {
        if (navigator.storage?.estimate) {
            const { usage = 0, quota = 0 } = await navigator.storage.estimate();
            return { usage, quota };
        }
    } catch { /* ignore */ }
    return null;
}
